'use client';

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import {
  MobileModalDebugBranch,
  MobileModalDebugEvent,
  captureMobileModalEnvironment,
} from '@/hooks/telemetry/mobile-modal-debug';
import type { PersistentDebugLogger } from '@/hooks/telemetry/use-persistent-debug-logger';
import { countOpenEntries, getModalStackIds } from './modal-stack-selectors';
import {
  ModalEntryPhase,
  type ModalEntry,
  type ModalStackContextValue,
} from './modal-stack-types';

interface UseModalStackProviderValueInput {
  modalDebugLogger: PersistentDebugLogger;
}

interface ModalStackState {
  stack: ModalEntry[];
  stackRef: MutableRefObject<ModalEntry[]>;
  syncStack: () => void;
}

interface ModalStackActionInput {
  modalDebugLogger: PersistentDebugLogger;
  stackRef: MutableRefObject<ModalEntry[]>;
  syncStack: () => void;
}

interface ModalStackRegistrationActions {
  markClosing: (id: string) => void;
  push: (entry: ModalEntry) => void;
  registerModal: (registration: ModalEntry) => void;
  unregisterModal: (id: string) => void;
}

interface ModalStackCloseActions {
  closeAll: () => number;
  closeN: (count: number) => number;
  getDepthCount: () => number;
  pop: (id?: string) => boolean;
  resolveToDepth: (targetDepth: number) => number;
}

interface ModalStackValueInput
  extends ModalStackRegistrationActions,
    ModalStackCloseActions {
  stack: ModalEntry[];
}

interface StackTransitionInput {
  nextStack: ModalEntry[];
  previousStack: ModalEntry[];
}

interface StackTransitionLoggerInput extends StackTransitionInput {
  modalDebugLogger: PersistentDebugLogger;
  name: MobileModalDebugEvent;
}

interface PopTargetInput {
  currentStack: ModalEntry[];
  id?: string;
  modalDebugLogger: PersistentDebugLogger;
  stackRef: MutableRefObject<ModalEntry[]>;
  syncStack: () => void;
  targetEntry: ModalEntry;
  targetIndex: number;
}

function upsertOpenModalEntry(input: {
  previousStack: ModalEntry[];
  registration: ModalEntry;
}): ModalEntry[] {
  const existingIndex = input.previousStack.findIndex(
    (entry) => entry.id === input.registration.id,
  );
  const openRegistration = {
    ...input.registration,
    phase: ModalEntryPhase.Open,
  };

  return existingIndex >= 0
    ? input.previousStack.map((entry, index) =>
        index === existingIndex ? openRegistration : entry,
      )
    : [...input.previousStack, openRegistration];
}

function replaceModalEntryPhase(input: {
  phase: ModalEntryPhase;
  stack: ModalEntry[];
  targetIndex: number;
}): ModalEntry[] {
  return input.stack.map((entry, index) =>
    index === input.targetIndex ? { ...entry, phase: input.phase } : entry,
  );
}

function findTopOpenModalIndex(stack: ModalEntry[]): number {
  for (let index = stack.length - 1; index >= 0; index -= 1) {
    if (stack[index]?.phase === ModalEntryPhase.Open) {
      return index;
    }
  }

  return -1;
}

function findPopTargetIndex({
  currentStack,
  id,
}: {
  currentStack: ModalEntry[];
  id?: string;
}): number {
  return id
    ? currentStack.findIndex(
        (entry) => entry.id === id && entry.phase === ModalEntryPhase.Open,
      )
    : findTopOpenModalIndex(currentStack);
}

function logStackRegistration({
  modalDebugLogger,
  nextStack,
  previousStack,
  registrationId,
}: StackTransitionInput & {
  modalDebugLogger: PersistentDebugLogger;
  registrationId: string;
}): void {
  modalDebugLogger.logStateTransition({
    name: MobileModalDebugEvent.ModalStackRegister,
    from: getModalStackIds(previousStack),
    to: getModalStackIds(nextStack),
    payload: {
      registrationId,
      previousDepth: previousStack.length,
      nextDepth: nextStack.length,
      environment: captureMobileModalEnvironment(),
    },
  });
}

function logStackIdTransition({
  id,
  modalDebugLogger,
  name,
  nextStack,
  previousStack,
}: StackTransitionLoggerInput & { id: string }): void {
  modalDebugLogger.logStateTransition({
    name,
    from: getModalStackIds(previousStack),
    to: getModalStackIds(nextStack),
    payload: {
      id,
      previousDepth: previousStack.length,
      nextDepth: nextStack.length,
      environment: captureMobileModalEnvironment(),
    },
  });
}

function logStackPopTransition({
  id,
  modalDebugLogger,
  nextStack,
  previousStack,
  targetEntry,
  targetIndex,
}: StackTransitionInput & {
  id: string | null;
  modalDebugLogger: PersistentDebugLogger;
  targetEntry: ModalEntry;
  targetIndex: number;
}): void {
  modalDebugLogger.logStateTransition({
    name: MobileModalDebugEvent.ModalStackPop,
    from: getModalStackIds(previousStack),
    to: getModalStackIds(nextStack),
    payload: {
      id,
      targetId: targetEntry.id,
      targetIndex,
      previousDepth: previousStack.length,
      nextDepth: nextStack.length,
      environment: captureMobileModalEnvironment(),
    },
  });
}

function useModalStackState(): ModalStackState {
  const stackRef = useRef<ModalEntry[]>([]);
  const [stack, setStack] = useState<ModalEntry[]>([]);
  const syncStack = useCallback(() => {
    setStack(stackRef.current);
  }, []);

  return { stack, stackRef, syncStack };
}

function useRegisterModalAction({
  modalDebugLogger,
  stackRef,
  syncStack,
}: ModalStackActionInput): (registration: ModalEntry) => void {
  return useCallback(
    (registration: ModalEntry): void => {
      const previousStack = stackRef.current;
      const nextStack = upsertOpenModalEntry({ previousStack, registration });
      logStackRegistration({
        modalDebugLogger,
        nextStack,
        previousStack,
        registrationId: registration.id,
      });
      stackRef.current = nextStack;
      syncStack();
    },
    [modalDebugLogger, stackRef, syncStack],
  );
}

function useMarkClosingAction({
  modalDebugLogger,
  stackRef,
  syncStack,
}: ModalStackActionInput): (id: string) => void {
  return useCallback(
    (id: string): void => {
      const previousStack = stackRef.current;
      const targetIndex = previousStack.findIndex((entry) => entry.id === id);
      if (
        targetIndex < 0 ||
        previousStack[targetIndex]?.phase === ModalEntryPhase.Closing
      ) {
        modalDebugLogger.logBranchChoice({
          name: MobileModalDebugEvent.ModalStackMarkClosing,
          selectedBranch: targetIndex < 0
            ? MobileModalDebugBranch.MissingId
            : MobileModalDebugBranch.AlreadyClosing,
          payload: {
            id,
            targetIndex,
            currentStack: getModalStackIds(previousStack),
            currentPhases: previousStack.map((entry) => entry.phase),
            environment: captureMobileModalEnvironment(),
          },
        });
        return;
      }

      const nextStack = replaceModalEntryPhase({
        phase: ModalEntryPhase.Closing,
        stack: previousStack,
        targetIndex,
      });
      modalDebugLogger.logStateTransition({
        name: MobileModalDebugEvent.ModalStackMarkClosing,
        from: previousStack.map((entry) => `${entry.id}:${entry.phase}`),
        to: nextStack.map((entry) => `${entry.id}:${entry.phase}`),
        payload: {
          id,
          targetIndex,
          environment: captureMobileModalEnvironment(),
        },
      });
      stackRef.current = nextStack;
      syncStack();
    },
    [modalDebugLogger, stackRef, syncStack],
  );
}

function useUnregisterModalAction({
  modalDebugLogger,
  stackRef,
  syncStack,
}: ModalStackActionInput): (id: string) => void {
  return useCallback(
    (id: string): void => {
      const previousStack = stackRef.current;
      const nextStack = previousStack.filter((entry) => entry.id !== id);
      if (nextStack.length === previousStack.length) {
        modalDebugLogger.logBranchChoice({
          name: MobileModalDebugEvent.ModalStackUnregister,
          selectedBranch: MobileModalDebugBranch.MissingId,
          payload: {
            id,
            currentStack: getModalStackIds(previousStack),
            currentDepth: previousStack.length,
            environment: captureMobileModalEnvironment(),
          },
        });
        return;
      }

      logStackIdTransition({
        modalDebugLogger,
        name: MobileModalDebugEvent.ModalStackUnregister,
        nextStack,
        previousStack,
        id,
      });
      stackRef.current = nextStack;
      syncStack();
    },
    [modalDebugLogger, stackRef, syncStack],
  );
}

function usePushModalAction({
  modalDebugLogger,
  registerModal,
  stackRef,
}: Pick<ModalStackActionInput, 'modalDebugLogger' | 'stackRef'> & {
  registerModal: (entry: ModalEntry) => void;
}): (entry: ModalEntry) => void {
  return useCallback(
    (entry: ModalEntry): void => {
      modalDebugLogger.logEventFlow({
        name: MobileModalDebugEvent.ModalStackPush,
        payload: {
          id: entry.id,
          currentStack: getModalStackIds(stackRef.current),
          environment: captureMobileModalEnvironment(),
        },
      });
      registerModal(entry);
    },
    [modalDebugLogger, registerModal, stackRef],
  );
}

function useModalRegistrationActions(
  input: ModalStackActionInput,
): ModalStackRegistrationActions {
  const registerModal = useRegisterModalAction(input);
  const markClosing = useMarkClosingAction(input);
  const unregisterModal = useUnregisterModalAction(input);
  const push = usePushModalAction({
    modalDebugLogger: input.modalDebugLogger,
    registerModal,
    stackRef: input.stackRef,
  });

  return useMemo(
    () => ({ markClosing, push, registerModal, unregisterModal }),
    [markClosing, push, registerModal, unregisterModal],
  );
}

function logMissingPopTarget({
  currentStack,
  id,
  modalDebugLogger,
}: {
  currentStack: ModalEntry[];
  id?: string;
  modalDebugLogger: PersistentDebugLogger;
}): false {
  modalDebugLogger.logBranchChoice({
    name: MobileModalDebugEvent.ModalStackPop,
    selectedBranch: MobileModalDebugBranch.MissingTarget,
    payload: {
      id: id ?? null,
      currentStack: getModalStackIds(currentStack),
      currentDepth: currentStack.length,
      environment: captureMobileModalEnvironment(),
    },
  });
  return false;
}

function logEmptyPopTarget({
  currentStack,
  id,
  modalDebugLogger,
  targetIndex,
}: {
  currentStack: ModalEntry[];
  id?: string;
  modalDebugLogger: PersistentDebugLogger;
  targetIndex: number;
}): false {
  modalDebugLogger.logBranchChoice({
    name: MobileModalDebugEvent.ModalStackPop,
    selectedBranch: MobileModalDebugBranch.TargetEntryEmpty,
    payload: {
      id: id ?? null,
      targetIndex,
      currentStack: getModalStackIds(currentStack),
      environment: captureMobileModalEnvironment(),
    },
  });
  return false;
}

function closePopTarget({
  currentStack,
  id,
  modalDebugLogger,
  stackRef,
  syncStack,
  targetEntry,
  targetIndex,
}: PopTargetInput): true {
  const nextStack = replaceModalEntryPhase({
    phase: ModalEntryPhase.Closing,
    stack: currentStack,
    targetIndex,
  });
  logStackPopTransition({
    modalDebugLogger,
    nextStack,
    previousStack: currentStack,
    id: id ?? null,
    targetEntry,
    targetIndex,
  });
  stackRef.current = nextStack;
  syncStack();
  targetEntry.onClose();
  return true;
}

function usePopModalAction({
  modalDebugLogger,
  stackRef,
  syncStack,
}: ModalStackActionInput): (id?: string) => boolean {
  return useCallback(
    (id?: string): boolean => {
      const currentStack = stackRef.current;
      const targetIndex = findPopTargetIndex({ currentStack, id });
      if (targetIndex < 0) {
        return logMissingPopTarget({ currentStack, id, modalDebugLogger });
      }

      const targetEntry = currentStack[targetIndex];
      if (!targetEntry) {
        return logEmptyPopTarget({
          currentStack,
          id,
          modalDebugLogger,
          targetIndex,
        });
      }

      return closePopTarget({
        currentStack,
        id,
        modalDebugLogger,
        stackRef,
        syncStack,
        targetEntry,
        targetIndex,
      });
    },
    [modalDebugLogger, stackRef, syncStack],
  );
}

function useModalCloseActions(
  input: ModalStackActionInput,
): ModalStackCloseActions {
  const { modalDebugLogger, stackRef } = input;
  const pop = usePopModalAction(input);
  const closeN = useCallback(
    (count: number): number => {
      if (count <= 0) {
        modalDebugLogger.logBranchChoice({
          name: MobileModalDebugEvent.ModalStackCloseN,
          selectedBranch: MobileModalDebugBranch.NonPositiveCount,
          payload: {
            count,
            currentDepth: stackRef.current.length,
            environment: captureMobileModalEnvironment(),
          },
        });
        return 0;
      }

      modalDebugLogger.logEventFlow({
        name: MobileModalDebugEvent.ModalStackCloseN,
        payload: {
          count,
          currentStack: getModalStackIds(stackRef.current),
          environment: captureMobileModalEnvironment(),
        },
      });
      let closedCount = 0;
      while (closedCount < count && pop()) {
        closedCount += 1;
      }

      return closedCount;
    },
    [modalDebugLogger, pop, stackRef],
  );

  return useModalCloseActionsResult({
    closeN,
    modalDebugLogger,
    pop,
    stackRef,
  });
}

function useModalCloseActionsResult({
  closeN,
  modalDebugLogger,
  pop,
  stackRef,
}: Pick<ModalStackActionInput, 'modalDebugLogger' | 'stackRef'> &
  Pick<ModalStackCloseActions, 'closeN' | 'pop'>): ModalStackCloseActions {
  const closeAll = useCallback((): number => {
    modalDebugLogger.logEventFlow({
      name: MobileModalDebugEvent.ModalStackCloseAll,
      payload: {
        currentStack: getModalStackIds(stackRef.current),
        currentDepth: stackRef.current.length,
        environment: captureMobileModalEnvironment(),
      },
    });
    return closeN(stackRef.current.length);
  }, [closeN, modalDebugLogger, stackRef]);

  const getDepthCount = useCallback((): number => {
    return countOpenEntries(stackRef.current);
  }, [stackRef]);

  const resolveToDepth = useResolveToDepthAction({
    closeN,
    modalDebugLogger,
    stackRef,
  });

  return useMemo(
    () => ({ closeAll, closeN, getDepthCount, pop, resolveToDepth }),
    [closeAll, closeN, getDepthCount, pop, resolveToDepth],
  );
}

function useResolveToDepthAction({
  closeN,
  modalDebugLogger,
  stackRef,
}: Pick<ModalStackActionInput, 'modalDebugLogger' | 'stackRef'> &
  Pick<ModalStackCloseActions, 'closeN'>): (targetDepth: number) => number {
  return useCallback(
    (targetDepth: number): number => {
      const normalizedTargetDepth = Math.max(0, targetDepth);
      const currentDepth = countOpenEntries(stackRef.current);
      if (normalizedTargetDepth >= currentDepth) {
        modalDebugLogger.logBranchChoice({
          name: MobileModalDebugEvent.ModalStackResolveToDepth,
          selectedBranch: MobileModalDebugBranch.AlreadyAtOrBelowTarget,
          payload: {
            targetDepth,
            normalizedTargetDepth,
            currentDepth,
            currentStack: getModalStackIds(stackRef.current),
            environment: captureMobileModalEnvironment(),
          },
        });
        return 0;
      }

      modalDebugLogger.logEventFlow({
        name: MobileModalDebugEvent.ModalStackResolveToDepth,
        payload: {
          targetDepth,
          normalizedTargetDepth,
          currentDepth,
          currentStack: getModalStackIds(stackRef.current),
          environment: captureMobileModalEnvironment(),
        },
      });
      return closeN(currentDepth - normalizedTargetDepth);
    },
    [closeN, modalDebugLogger, stackRef],
  );
}

function useModalStackValue({
  closeAll,
  closeN,
  getDepthCount,
  markClosing,
  pop,
  push,
  registerModal,
  resolveToDepth,
  stack,
  unregisterModal,
}: ModalStackValueInput): ModalStackContextValue {
  return useMemo<ModalStackContextValue>(
    () => ({
      stack,
      depth: countOpenEntries(stack),
      pop,
      closeAll,
      closeN,
      getDepthCount,
      resolveToDepth,
      push,
      registerModal,
      markClosing,
      unregisterModal,
    }),
    [
      closeAll,
      closeN,
      getDepthCount,
      markClosing,
      pop,
      push,
      registerModal,
      resolveToDepth,
      stack,
      unregisterModal,
    ],
  );
}

export function useModalStackProviderValue({
  modalDebugLogger,
}: UseModalStackProviderValueInput): ModalStackContextValue {
  const stackState = useModalStackState();
  const { stack, stackRef, syncStack } = stackState;
  const actionInput = useMemo(
    () => ({
      modalDebugLogger,
      stackRef,
      syncStack,
    }),
    [modalDebugLogger, stackRef, syncStack],
  );
  const registrationActions = useModalRegistrationActions(actionInput);
  const closeActions = useModalCloseActions(actionInput);

  return useModalStackValue({
    stack,
    ...registrationActions,
    ...closeActions,
  });
}
